/**
 * Le classement et les fiches de joueur.
 *
 * Tout vient du serveur, et de sa table `progression` — le **miroir bridé**
 * (voir `src/sim/verif.ts`), jamais des sauvegardes. Le client ne calcule rien
 * ici : ce serait précisément la faille qu'on vient de fermer.
 *
 * Les données sont mises en cache le temps d'une consultation : un classement
 * n'a pas besoin d'être à la seconde, et le rafraîchir à chaque image ferait
 * une requête toutes les cent millisecondes.
 */
export interface LigneClassement {
  rang: number
  pseudo: string
  niveau: number
  xp: number
  kills: number
  donjons: number
  personnages: number
  points: number
  maj_le: number
}

export interface Profil {
  pseudo: string
  cree_le: number
  niveau: number
  xp: number
  kills: number
  donjons: number
  personnages: number
  kamas: number
  points: number
  maj_le: number
  succes: string[]
  vitrine: {
    nom: string
    classe: number
    niveau: number
    kills: number
    equipement: Record<string, { item: number; jet: Record<number, number> }>
  }[]
}

export const TRIS: [cle: string, libelle: string][] = [
  ["points", "Succès"], ["niveau", "Niveau"], ["donjons", "Donjons"], ["kills", "Victoires"],
]

/** Durée de vie du cache : au-delà, on redemande. */
const FRAICHEUR_MS = 30_000

const cache = new Map<string, { le: number; data: unknown }>()

async function lire<T>(chemin: string): Promise<T | { erreur: string }> {
  const v = cache.get(chemin)
  if (v && Date.now() - v.le < FRAICHEUR_MS) return v.data as T
  try {
    const r = await fetch(chemin)
    const data = await r.json()
    if (r.ok) cache.set(chemin, { le: Date.now(), data })
    return data as T
  } catch {
    return { erreur: "Le classement n'a pas répondu. Vérifie ta connexion." }
  }
}

export const lireClassement = (tri: string) =>
  lire<{ tri: string; joueurs: LigneClassement[] }>(`/api/classement?tri=${encodeURIComponent(tri)}&limite=50`)

export const lireProfil = (pseudo: string) =>
  lire<Profil>(`/api/profil?pseudo=${encodeURIComponent(pseudo)}`)

/** À appeler quand on veut forcer un rafraîchissement. */
export const oublierCache = () => cache.clear()
